/**
 * API Service for communicating with the backend
 */

// API URL from environment variable (required)
const API_URL = process.env.REACT_APP_BACKEND_URL;

if (!API_URL) {
  console.warn('REACT_APP_BACKEND_URL not set. API calls may fail.');
}

// Frontend version (synced with backend)
export const FRONTEND_VERSION = "1.0.0";

class ApiService {
  constructor() {
    this.baseUrl = API_URL;
    this.token = null;
  }

  setToken(token) {
    this.token = token;
    if (token) {
      localStorage.setItem('access_token', token);
    } else {
      localStorage.removeItem('access_token');
    }
  }

  getToken() {
    if (!this.token) {
      this.token = localStorage.getItem('access_token');
    }
    return this.token;
  }

  async request(endpoint, options = {}) {
    const url = `${this.baseUrl}${endpoint}`;
    const token = this.getToken();

    const headers = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    try {
      const response = await fetch(url, {
        ...options,
        headers,
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({ detail: 'Erro desconhecido' }));
        throw new Error(error.detail || `HTTP ${response.status}`);
      }

      if (response.status === 204) {
        return null;
      }

      return await response.json();
    } catch (error) {
      console.error(`API Error [${endpoint}]:`, error);
      throw error;
    }
  }

  // Health & Version
  async getHealth() {
    return this.request('/api/health');
  }

  async getVersion() {
    return this.request('/api/version');
  }

  // Auth
  async login(email, password) {
    const data = await this.request('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
    if (data.access_token) {
      this.setToken(data.access_token);
      localStorage.setItem('refresh_token', data.refresh_token);
    }
    return data;
  }

  async register(email, password, nome, telefone = null) {
    return this.request('/api/auth/register', {
      method: 'POST',
      body: JSON.stringify({ email, password, nome, telefone }),
    });
  }

  async getMe() {
    return this.request('/api/auth/me');
  }

  async refreshToken() {
    const refreshToken = localStorage.getItem('refresh_token');
    if (!refreshToken) {
      throw new Error('No refresh token');
    }
    const data = await this.request('/api/auth/refresh', {
      method: 'POST',
      body: JSON.stringify({ refresh_token: refreshToken }),
    });
    if (data.access_token) {
      this.setToken(data.access_token);
      localStorage.setItem('refresh_token', data.refresh_token);
    }
    return data;
  }

  logout() {
    this.setToken(null);
    localStorage.removeItem('refresh_token');
  }

  // Igrejas
  async getIgrejas() {
    return this.request('/api/igrejas');
  }

  async getMinhasIgrejas() {
    return this.request('/api/igrejas/minhas');
  }

  async getIgreja(id) {
    return this.request(`/api/igrejas/${id}`);
  }

  async createIgreja(data) {
    return this.request('/api/igrejas', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateIgreja(id, data) {
    return this.request(`/api/igrejas/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteIgreja(id) {
    return this.request(`/api/igrejas/${id}`, { method: 'DELETE' });
  }

  async getIgrejaStats(id) {
    return this.request(`/api/igrejas/${id}/estatisticas`);
  }

  async getIgrejaDashboard(id) {
    return this.request(`/api/igrejas/${id}/dashboard`);
  }

  // Categorias
  async getCategorias(igrejaId) {
    return this.request(`/api/igrejas/${igrejaId}/categorias`);
  }

  async createCategoria(igrejaId, data) {
    return this.request(`/api/igrejas/${igrejaId}/categorias`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateCategoria(igrejaId, categoriaId, data) {
    return this.request(`/api/igrejas/${igrejaId}/categorias/${categoriaId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteCategoria(igrejaId, categoriaId) {
    return this.request(`/api/igrejas/${igrejaId}/categorias/${categoriaId}`, {
      method: 'DELETE',
    });
  }

  // Igreja Membros
  async getIgrejaMembros(igrejaId) {
    return this.request(`/api/igrejas/${igrejaId}/membros`);
  }

  async addIgrejaMembro(igrejaId, userId, role = 'membro') {
    return this.request(`/api/igrejas/${igrejaId}/membros`, {
      method: 'POST',
      body: JSON.stringify({ user_id: userId, role }),
    });
  }

  async updateIgrejaMembroRole(igrejaId, membroId, role) {
    return this.request(`/api/igrejas/${igrejaId}/membros/${membroId}/role?role=${role}`, {
      method: 'PUT',
    });
  }

  async removeIgrejaMembro(igrejaId, membroId) {
    return this.request(`/api/igrejas/${igrejaId}/membros/${membroId}`, {
      method: 'DELETE',
    });
  }

  // Células
  async getCelulas(igrejaId, categoriaIds = null) {
    let url = `/api/igrejas/${igrejaId}/celulas`;
    if (categoriaIds && categoriaIds.length > 0) {
      url += `?categoria_ids=${categoriaIds.join(',')}`;
    }
    return this.request(url);
  }

  async getCelula(igrejaId, celulaId) {
    return this.request(`/api/igrejas/${igrejaId}/celulas/${celulaId}`);
  }

  async createCelula(igrejaId, data) {
    return this.request(`/api/igrejas/${igrejaId}/celulas`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateCelula(igrejaId, celulaId, data) {
    return this.request(`/api/igrejas/${igrejaId}/celulas/${celulaId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteCelula(igrejaId, celulaId) {
    return this.request(`/api/igrejas/${igrejaId}/celulas/${celulaId}`, {
      method: 'DELETE',
    });
  }

  async getCelulaStats(igrejaId, celulaId) {
    return this.request(`/api/igrejas/${igrejaId}/celulas/${celulaId}/estatisticas`);
  }

  async searchCelulas(igrejaId, nome) {
    return this.request(`/api/igrejas/${igrejaId}/celulas/search?nome=${encodeURIComponent(nome)}`);
  }

  // Célula Membros
  async getCelulaMembros(igrejaId, celulaId) {
    return this.request(`/api/igrejas/${igrejaId}/celulas/${celulaId}/membros`);
  }

  async addCelulaMembro(igrejaId, celulaId, igrejaMembroId, role = 'membro') {
    return this.request(`/api/igrejas/${igrejaId}/celulas/${celulaId}/membros`, {
      method: 'POST',
      body: JSON.stringify({ igreja_membro_id: igrejaMembroId, role }),
    });
  }

  async updateCelulaMembroRole(igrejaId, celulaId, membroId, role) {
    return this.request(`/api/igrejas/${igrejaId}/celulas/${celulaId}/membros/${membroId}/role?role=${role}`, {
      method: 'PUT',
    });
  }

  async removeCelulaMembro(igrejaId, celulaId, membroId) {
    return this.request(`/api/igrejas/${igrejaId}/celulas/${celulaId}/membros/${membroId}`, {
      method: 'DELETE',
    });
  }

  // Encontros
  async getEncontros(igrejaId, celulaId, status = null) {
    let url = `/api/igrejas/${igrejaId}/celulas/${celulaId}/encontros`;
    if (status) {
      url += `?status=${status}`;
    }
    return this.request(url);
  }

  async getProximosEncontros(igrejaId, celulaId, limite = 5) {
    return this.request(`/api/igrejas/${igrejaId}/celulas/${celulaId}/encontros/proximos?limite=${limite}`);
  }

  async getEncontro(encontroId) {
    // Direct endpoint - doesn't require celula_id
    return this.request(`/api/encontros/${encontroId}`);
  }

  async getEncontroByCelula(igrejaId, celulaId, encontroId) {
    return this.request(`/api/igrejas/${igrejaId}/celulas/${celulaId}/encontros/${encontroId}`);
  }

  async createEncontro(igrejaId, celulaId, data) {
    return this.request(`/api/igrejas/${igrejaId}/celulas/${celulaId}/encontros`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateEncontro(igrejaId, celulaId, encontroId, data) {
    return this.request(`/api/igrejas/${igrejaId}/celulas/${celulaId}/encontros/${encontroId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteEncontro(igrejaId, celulaId, encontroId) {
    return this.request(`/api/igrejas/${igrejaId}/celulas/${celulaId}/encontros/${encontroId}`, {
      method: 'DELETE',
    });
  }

  async registrarPresenca(igrejaId, celulaId, encontroId, membroId, presente) {
    return this.request(`/api/igrejas/${igrejaId}/celulas/${celulaId}/encontros/${encontroId}/presenca`, {
      method: 'POST',
      body: JSON.stringify({ membro_id: membroId, presente }),
    });
  }

  async finalizarEncontro(igrejaId, celulaId, encontroId) {
    return this.request(`/api/igrejas/${igrejaId}/celulas/${celulaId}/encontros/${encontroId}/finalizar`, {
      method: 'POST',
    });
  }

  // Admin (Master only)
  async getAdminDashboard() {
    return this.request('/api/admin/dashboard');
  }

  async getAdminUsers() {
    return this.request('/api/admin/usuarios');
  }

  async toggleUserActive(userId) {
    return this.request(`/api/admin/usuarios/${userId}/toggle-active`, {
      method: 'PUT',
    });
  }

  async updateUserRole(userId, role) {
    return this.request(`/api/admin/usuarios/${userId}/role?role=${role}`, {
      method: 'PUT',
    });
  }

  // Notifications
  async getNotifications(unreadOnly = false) {
    const query = unreadOnly ? '?unread_only=true' : '';
    return this.request(`/api/notifications${query}`);
  }

  async getNotificationCount() {
    return this.request('/api/notifications/count');
  }

  async markNotificationRead(notificationId) {
    return this.request(`/api/notifications/${notificationId}/read`, {
      method: 'POST',
    });
  }

  async markAllNotificationsRead() {
    return this.request('/api/notifications/read-all', {
      method: 'POST',
    });
  }

  async deleteNotification(notificationId) {
    return this.request(`/api/notifications/${notificationId}`, {
      method: 'DELETE',
    });
  }
}

export const apiService = new ApiService();
export default apiService;
